// src/components/Features/FlashRTC.tsx - Flash RTC out-of-band communication for Gold+ users
import React, { useState } from 'react';
import { useSubscriptionContext } from '../../contexts/SubscriptionContext';
import SubscriptionUpgradeModal from '../SubscriptionUpgradeModal';

interface FlashRTCProps {
  isOpen: boolean;
  onClose: () => void;
}

const FlashRTC: React.FC<FlashRTCProps> = ({ isOpen, onClose }) => {
  const { canAccessFeature } = useSubscriptionContext();
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [message, setMessage] = useState('');
  const [recipients, setRecipients] = useState<string[]>([]);
  const [newRecipient, setNewRecipient] = useState('');

  const canAccess = canAccessFeature('flash_rtc');

  const handleSendMessage = () => {
    if (!canAccess) {
      setShowUpgradeModal(true);
      return;
    }

    // Simulate sending message through Flash RTC
    console.log('Sending Flash RTC message:', { message, recipients });
    alert('Message sent through Flash RTC!');
    setMessage('');
    setRecipients([]);
    onClose();
  };

  // Show upgrade modal immediately if user doesn't have access
  React.useEffect(() => {
    if (isOpen && !canAccess) {
      setShowUpgradeModal(true);
    }
  }, [isOpen, canAccess]);

  const addRecipient = () => {
    if (newRecipient.trim() && !recipients.includes(newRecipient.trim())) {
      setRecipients([...recipients, newRecipient.trim()]);
      setNewRecipient('');
    }
  };

  const removeRecipient = (recipient: string) => {
    setRecipients(recipients.filter(r => r !== recipient));
  };

  if (!isOpen) return null;

  return (
    <>
      <div className="fixed inset-0 z-50 overflow-y-auto">
        <div className="flex min-h-screen items-center justify-center p-4">
          {/* Backdrop */}
          <div
            className="fixed inset-0 bg-black bg-opacity-50 transition-opacity"
            onClick={onClose}
          />

          {/* Modal */}
          <div className="relative w-full max-w-2xl bg-white rounded-2xl shadow-2xl">
            {/* Header */}
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <div className="flex items-center space-x-3">
                <div className="flex justify-center items-center w-10 h-10 bg-gradient-to-r from-yellow-500 to-orange-500 rounded-xl">
                  <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                  </svg>
                </div>
                <div>
                  <h2 className="text-xl font-semibold text-gray-900">Flash RTC</h2>
                  <p className="text-sm text-gray-600">Out-of-band emergency communication</p>
                </div>
              </div>
              <button
                onClick={onClose}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Content */}
            <div className="p-6">
              {!canAccess ? (
                <div className="text-center py-8">
                  <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                    </svg>
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">Gold Plan Required</h3>
                  <p className="text-gray-600 mb-6">
                    Flash RTC out-of-band communication is available for Gold and Premium subscribers.
                  </p>
                  <button
                    onClick={() => setShowUpgradeModal(true)}
                    className="px-6 py-3 bg-gradient-to-r from-yellow-500 to-orange-500 text-white font-semibold rounded-lg hover:from-yellow-600 hover:to-orange-600 transition-all duration-200"
                  >
                    Upgrade to Gold Plan
                  </button>
                </div>
              ) : (
                <div className="space-y-6">
                  {/* Recipients */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Emergency Contacts
                    </label>
                    <div className="flex space-x-2 mb-2">
                      <input
                        type="email"
                        value={newRecipient}
                        onChange={(e) => setNewRecipient(e.target.value)}
                        placeholder="Enter email address"
                        className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-transparent"
                        onKeyPress={(e) => e.key === 'Enter' && addRecipient()}
                      />
                      <button
                        onClick={addRecipient}
                        className="px-4 py-2 bg-yellow-500 text-white rounded-lg hover:bg-yellow-600 transition-colors"
                      >
                        Add
                      </button>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {recipients.map((recipient, index) => (
                        <span
                          key={index}
                          className="inline-flex items-center px-3 py-1 bg-yellow-100 text-yellow-800 rounded-full text-sm"
                        >
                          {recipient}
                          <button
                            onClick={() => removeRecipient(recipient)}
                            className="ml-2 text-yellow-600 hover:text-yellow-800"
                          >
                            ×
                          </button>
                        </span>
                      ))}
                    </div>
                  </div>

                  {/* Message */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Emergency Message
                    </label>
                    <textarea
                      value={message}
                      onChange={(e) => setMessage(e.target.value)}
                      placeholder="Type your emergency message here..."
                      rows={4}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-transparent"
                    />
                  </div>

                  {/* Features */}
                  <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                    <h4 className="font-semibold text-yellow-900 mb-2">Flash RTC Features:</h4>
                    <ul className="text-sm text-yellow-800 space-y-1">
                      <li>• Out-of-band communication during emergencies</li>
                      <li>• Multi-channel delivery (SMS, Email, Push)</li>
                      <li>• Priority routing for critical messages</li>
                      <li>• Delivery confirmation and read receipts</li>
                    </ul>
                  </div>

                  {/* Actions */}
                  <div className="flex justify-end space-x-3">
                    <button
                      onClick={onClose}
                      className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleSendMessage}
                      disabled={!message.trim() || recipients.length === 0}
                      className="px-6 py-2 bg-gradient-to-r from-yellow-500 to-orange-500 text-white font-semibold rounded-lg hover:from-yellow-600 hover:to-orange-600 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Send Emergency Message
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Upgrade Modal */}
      <SubscriptionUpgradeModal
        isOpen={showUpgradeModal}
        onClose={() => setShowUpgradeModal(false)}
        requiredFeature="gold"
        featureName="Flash RTC Communication"
      />
    </>
  );
};

export default FlashRTC;
